import { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useConsentTemplateAccess } from '@/features/consent-templates/access';
import {
  useConsentTemplateAction,
  useConsentTemplateHistory,
} from '@/features/consent-templates/api';
import { groupByKey } from '@/features/consent-templates/group';
import type {
  ConsentTemplate,
  ConsentTemplateGroup,
} from '@/features/consent-templates/types';
import { isApiError } from '@/lib/api';
import { FailureBox } from './ConsentFailureBox';
import { ConsentTemplateForm, type FormMode } from './ConsentTemplateForm';
import { ConsentWordingPreview } from './ConsentWordingPreview';
import {
  CONSENT_ERRORS,
  procedureName,
  RELOAD_CODES,
  STATUS_LABEL,
  STATUS_TONE,
  toFailure,
  when,
  type Failure,
} from './consent-format';
import styles from './ConsentTemplates.module.css';

/**
 * One consent template key (RL-1136): every revision (history), the chosen
 * revision's wording with an English / Bahasa Melayu preview, and the actions
 * its status allows. Every action sends the revision's `version`; on 409 the
 * history is reloaded.
 */
export function ConsentTemplateDrawer({
  templateKey,
  onClose,
}: {
  templateKey: string;
  onClose: () => void;
}) {
  const q = useConsentTemplateHistory(templateKey);
  const group = q.data?.length ? groupByKey(q.data)[0] : undefined;

  return group ? (
    <KeyDialog
      group={group}
      reloading={q.isFetching}
      reload={() => void q.refetch()}
      onClose={onClose}
    />
  ) : (
    <Dialog open onClose={onClose} title="Consent template">
      {q.isLoading ? (
        <LoadingState label="Loading the template" />
      ) : (
        <ErrorState
          error={q.error}
          title={
            q.error ? undefined : CONSENT_ERRORS.CONSENT_TEMPLATE_NOT_FOUND
          }
          onRetry={() => void q.refetch()}
        />
      )}
    </Dialog>
  );
}

type Confirm = 'approve' | 'retire' | null;

function KeyDialog({
  group,
  reloading,
  reload,
  onClose,
}: {
  group: ConsentTemplateGroup;
  reloading: boolean;
  reload: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const access = useConsentTemplateAccess();
  const approve = useConsentTemplateAction('approve');
  const retire = useConsentTemplateAction('retire');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [form, setForm] = useState<FormMode | null>(null);

  const t =
    group.revisions.find((r) => r.id === selectedId) ??
    group.draft ??
    group.approved ??
    group.latest;
  const busy = approve.isPending || retire.isPending;

  function select(id: string) {
    setSelectedId(id);
    setConfirm(null);
    setFailure(null);
  }

  async function run(action: 'approve' | 'retire') {
    setFailure(null);
    const mutation = action === 'approve' ? approve : retire;
    try {
      const saved = await mutation.mutateAsync({
        id: t.id,
        version: t.version,
      });
      setConfirm(null);
      toast.show({
        tone: 'success',
        title:
          action === 'approve'
            ? `Revision ${saved.revision} approved: patients sign this wording from now on`
            : t.status === 'draft'
              ? `Draft revision ${saved.revision} discarded`
              : `Revision ${saved.revision} retired`,
      });
    } catch (err) {
      setConfirm(null);
      setFailure(toFailure(err));
      if (isApiError(err) && RELOAD_CODES.has(err.code)) reload();
    }
  }

  const canRevise = access.canManage && !group.draft;
  const footer = confirm ? (
    <>
      <Button onClick={() => setConfirm(null)} disabled={busy}>
        Cancel
      </Button>
      <Button
        variant={confirm === 'approve' ? 'primary' : 'danger'}
        loading={busy}
        onClick={() => void run(confirm)}
      >
        {confirm === 'approve'
          ? `Approve revision ${t.revision}`
          : t.status === 'draft'
            ? 'Discard draft'
            : `Retire revision ${t.revision}`}
      </Button>
    </>
  ) : (
    <>
      {t.status === 'draft' && access.canManage && (
        <>
          <Button onClick={() => setConfirm('retire')}>Discard draft</Button>
          <Button onClick={() => setForm({ kind: 'edit', template: t })}>
            Edit draft
          </Button>
        </>
      )}
      {t.status === 'draft' && access.canApprove && (
        <Button variant="primary" onClick={() => setConfirm('approve')}>
          Approve
        </Button>
      )}
      {t.status === 'approved' && access.canManage && (
        <Button variant="danger" onClick={() => setConfirm('retire')}>
          Retire
        </Button>
      )}
      {t.status !== 'draft' && canRevise && (
        <Button
          variant="primary"
          onClick={() => setForm({ kind: 'revise', base: t })}
        >
          New revision
        </Button>
      )}
      {!access.canManage && !access.canApprove && (
        <Button onClick={onClose}>Close</Button>
      )}
    </>
  );

  return (
    <>
      <Dialog
        open={!form}
        onClose={onClose}
        dismissible={!confirm}
        wide
        title={`${t.title.en} · revision ${t.revision}`}
        footer={footer}
      >
        <div className={styles.drawer}>
          {failure && <FailureBox failure={failure} />}
          {reloading && <p className={styles.muted}>Reloading the template…</p>}
          {confirm && <ConfirmNote confirm={confirm} t={t} group={group} />}
          {t.status === 'draft' && !access.canApprove && (
            <p className={styles.note}>
              A doctor approves this draft before patients can sign it.
            </p>
          )}
          {t.status !== 'draft' && access.canManage && group.draft && (
            <p className={styles.note}>
              Revision {group.draft.revision} is a draft: edit, approve or
              discard it before starting another revision.
            </p>
          )}
          <Summary t={t} />
          <h3 className={styles.section}>Wording</h3>
          <ConsentWordingPreview wording={t} />
          <h3 className={styles.section}>History</h3>
          <History group={group} selected={t.id} onSelect={select} />
        </div>
      </Dialog>
      {form && (
        <ConsentTemplateForm
          mode={form}
          onClose={() => setForm(null)}
          onSaved={(saved) => {
            setForm(null);
            select(saved.id);
          }}
        />
      )}
    </>
  );
}

function ConfirmNote({
  confirm,
  t,
  group,
}: {
  confirm: Exclude<Confirm, null>;
  t: ConsentTemplate;
  group: ConsentTemplateGroup;
}) {
  if (confirm === 'approve')
    return (
      <div className={styles.confirm} role="status">
        <strong>Approve revision {t.revision}?</strong>
        Patients read and sign this wording in the app from now on.
        {group.approved &&
          ` Revision ${group.approved.revision} is retired in the same step; forms already signed keep the revision they signed.`}
      </div>
    );
  return (
    <div className={styles.confirm} role="status">
      <strong>
        {t.status === 'draft'
          ? `Discard draft revision ${t.revision}?`
          : `Retire revision ${t.revision}?`}
      </strong>
      {t.status === 'draft'
        ? 'The draft is kept in the history as retired and is never offered to patients.'
        : 'It is no longer offered to patients: bookings that need it ask the patient to sign at the clinic until another revision is approved. Forms already signed stay valid.'}
    </div>
  );
}

function Summary({ t }: { t: ConsentTemplate }) {
  return (
    <dl className={styles.kv}>
      <dt>Status</dt>
      <dd>
        <Badge tone={STATUS_TONE[t.status]} dot>
          {STATUS_LABEL[t.status]}
        </Badge>
      </dd>
      <dt>Key</dt>
      <dd className="mono">{t.key}</dd>
      <dt>Procedure</dt>
      <dd>{procedureName(t.procedureCode)}</dd>
      <dt>Service</dt>
      <dd>
        {t.service?.name ?? (
          <span className={styles.muted}>
            None: only bookings the desk points at this template
          </span>
        )}
      </dd>
      <dt>Signed</dt>
      <dd>
        {t.signedCount} {t.signedCount === 1 ? 'form' : 'forms'}
      </dd>
    </dl>
  );
}

function History({
  group,
  selected,
  onSelect,
}: {
  group: ConsentTemplateGroup;
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <ul className={styles.history} aria-label="Revisions">
      {group.revisions.map((r) => (
        <li key={r.id} aria-current={r.id === selected ? 'true' : undefined}>
          <strong>Revision {r.revision}</strong>
          <Badge tone={STATUS_TONE[r.status]} dot>
            {STATUS_LABEL[r.status]}
          </Badge>
          {r.id !== selected && (
            <Button
              size="sm"
              onClick={() => onSelect(r.id)}
              aria-label={`View revision ${r.revision}`}
            >
              View
            </Button>
          )}
          <span>
            Written by {r.createdBy.fullName}, {when(r.createdAt)}
          </span>
          {r.approvedAt && (
            <span>
              Approved by {r.approvedBy?.fullName ?? 'a doctor'},{' '}
              {when(r.approvedAt)}
            </span>
          )}
          {r.retiredAt && (
            <span>
              {r.approvedAt ? 'Retired' : 'Discarded'} by{' '}
              {r.retiredBy?.fullName ?? 'staff'}, {when(r.retiredAt)}
            </span>
          )}
          {r.signedCount > 0 && (
            <span>
              {r.signedCount} signed {r.signedCount === 1 ? 'form' : 'forms'}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
